import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { isNoiseMessage } from './chat/messageNoise';
import { withVoiceMarker, stripVoiceMarker, wasSpoken } from './chat/voiceMarker';
import { useWalkieTalkie, TALK_LABEL } from '../hooks/useWalkieTalkie';
import { speechQueue } from '../voice/speechQueue';
import '../styles/alexdock.css';   // mikrofonknappens stilar delas med panelen
import ReactMarkdown from 'react-markdown';
import { CollapsibleMarkdown } from './chat/CollapsibleMarkdown';
import remarkGfm from 'remark-gfm';
import type { UseGatewayResult } from '../gateway/useGateway';
import { useAlexGateway } from '../gateway/AlexGateway';
import type { ChatAttachment } from '../gateway/gatewaySocket';
import {
    Zap,
    Cloud,
    Mic,
    Plus,
    Paperclip,
    Image,
    FileCode,

    SendHorizontal,
    Square,
    X,
    File as FileIcon,
} from 'lucide-react';

interface Props {
    onTaskCreated: () => void;
    gateway?: UseGatewayResult;
    /* Erbjuds bara när en moln-Alex finns att gå till. Växlingen sker ALDRIG
       av sig själv — det var hela felet med den gamla fallbacken: man visste
       inte vilken Alex man pratade med. Knappen gör valet synligt. */
    onUseCloudAlex?: () => void;
}

function formatTime(ts?: string): string {
    if (!ts) return '';
    try {
        const d = new Date(ts);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}

const STATUS_LABELS: Record<string, string> = {
    connected: 'Online',
    connecting: 'Connecting…',
    disconnected: 'Offline',
};

const ALEX_STATE_LABELS: Record<string, string> = {
    idle: 'Idle',
    thinking: 'Thinking…',
    executing: 'Running tools…',
    stuck: 'Stuck',
    unknown: '',
};

/* ─── Attach Menu ─── */
function AttachMenu({ onImageSelect, onFileSelect }: {
    onImageSelect: () => void;
    onFileSelect: () => void;
}) {
    const [isOpen, setIsOpen] = useState(false);

    const items = [
        { icon: <Paperclip size={14} />, label: 'Ladda upp fil', action: onFileSelect },
        { icon: <Image size={14} />, label: 'Lägg till bild', action: onImageSelect },
        { icon: <FileCode size={14} />, label: 'Importera kod', action: onFileSelect },
    ];

    return (
        <div className="bolt-attach">
            <button
                className="bolt-attach-trigger"
                onClick={() => setIsOpen(!isOpen)}
                title="Bifoga"
            >
                <Plus size={14} className={isOpen ? 'bolt-rotate-45' : ''} />
            </button>

            {isOpen && (
                <>
                    <div className="bolt-dropdown-backdrop" onClick={() => setIsOpen(false)} />
                    <div className="bolt-attach-dropdown">
                        {items.map((item, i) => (
                            <button key={i} className="bolt-attach-option" onClick={() => { item.action(); setIsOpen(false); }}>
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/* ─── Memoized Message (prevents ReactMarkdown re-renders on input change) ─── */
const remarkPlugins = [remarkGfm];
const MemoMessage = memo(function ChatMessage({ msg }: { msg: { role: string; content: string; timestamp?: string; attachments?: ChatAttachment[] } }) {
    return (
        <div className={`chat-message ${msg.role} ${msg.role === 'assistant' ? 'alex' : ''}`}>
            {msg.attachments && msg.attachments.length > 0 && (
                <div className="msg-attachments">
                    {msg.attachments.filter(a => a.preview).map(a => (
                        <img key={a.id} src={a.preview} alt={a.name} className="msg-attachment-img" />
                    ))}
                    {msg.attachments.filter(a => !a.preview).map(a => (
                        <div key={a.id} className="msg-attachment-file">
                            <FileIcon size={12} />
                            <span>{a.name}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="message-content markdown-body">
                {msg.role === 'assistant'
                    ? <CollapsibleMarkdown content={msg.content} />
                    : (
                        <>
                            {wasSpoken(msg.content) && <Mic size={11} className="msg-spoken-icon" />}
                            <ReactMarkdown remarkPlugins={remarkPlugins}>{stripVoiceMarker(msg.content)}</ReactMarkdown>
                        </>
                    )}
            </div>
            {msg.timestamp && <span className="msg-timestamp">{formatTime(msg.timestamp)}</span>}
        </div>
    );
});

/* ─── Main Component ─── */
export function AlexChat({ gateway: externalGateway, onUseCloudAlex }: Props) {
    const [input, setInput] = useState('');

    // Alex state (WebSocket) — use external gateway if provided, otherwise internal
    const internalGateway = useAlexGateway();
    const gateway = externalGateway || internalGateway;

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Attachments state
    const [attachments, setAttachments] = useState<ChatAttachment[]>([]);

    // File → base64 helper
    const fileToAttachment = useCallback((file: File): Promise<ChatAttachment> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const b64 = (reader.result as string).split(',')[1] || '';
                const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
                resolve({
                    id: crypto.randomUUID(),
                    name: file.name,
                    type: file.type || 'application/octet-stream',
                    size: file.size,
                    data_b64: b64,
                    preview,
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }, []);

    // Paste handler (Cmd+V screenshots)
    const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
        const files = Array.from(e.clipboardData.files).filter(f => f.type.startsWith('image/'));
        if (!files.length) return;
        e.preventDefault();
        const newAttachments = await Promise.all(files.map(fileToAttachment));
        setAttachments(prev => [...prev, ...newAttachments]);
    }, [fileToAttachment]);

    // File input handlers
    const handleFilesSelected = useCallback(async (files: FileList | null) => {
        if (!files?.length) return;
        const newAttachments = await Promise.all(Array.from(files).map(fileToAttachment));
        setAttachments(prev => [...prev, ...newAttachments]);
    }, [fileToAttachment]);

    const removeAttachment = useCallback((id: string) => {
        setAttachments(prev => {
            const att = prev.find(a => a.id === id);
            if (att?.preview) URL.revokeObjectURL(att.preview);
            return prev.filter(a => a.id !== id);
        });
    }, []);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [gateway.messages, gateway.streamingContent]);

    // Auto-resize textarea
    const autoResize = useCallback(() => {
        const ta = textareaRef.current;
        if (ta) {
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
        }
    }, []);

    useEffect(() => { autoResize(); }, [input, autoResize]);

    // --- Send handler ---
    const send = useCallback((text: string, spoken = false) => {
        if (gateway.status !== 'connected' || gateway.isStreaming || (!text.trim() && !attachments.length)) return;
        const atts = attachments.length ? [...attachments] : undefined;
        setInput('');
        setAttachments([]);
        // Talat in? Då ska svaret läsas upp. Kön nollställs här, matas av
        // gatewayns strömmande deltas nedan och töms när svaret är klart.
        spokenRef.current = spoken;
        if (spoken) speechQueue.start(); else speechQueue.stop();
        // Talat in? Säg det till agenten — annars svarar han som på skrift.
        gateway.sendMessage(spoken ? withVoiceMarker(text) : text.trim(), atts);
    }, [attachments, gateway]);

    const handleSend = () => send(input);

    // Håll-och-prata mot gateway-Alex. Samma hook som i den flytande panelen:
    // mikrofon → transkribering → in i DEN HÄR tråden. Svaret kommer som
    // strömmande deltas, inte som ett returvärde, så uppläsningen sköts av
    // effekten under (därför null här).
    const spokenRef = useRef(false);
    const talk = useWalkieTalkie({
        onTranscript: async (t) => { send(t, true); return null; },
    });
    const [queueSpeaking, setQueueSpeaking] = useState(false);
    useEffect(() => speechQueue.onState(setQueueSpeaking), []);

    // Mata uppläsningen med det som redan skrivits ut, mening för mening.
    const spokenUpToRef = useRef(0);
    useEffect(() => {
        if (!spokenRef.current) return;
        const text = gateway.streamingContent;
        if (text.length > spokenUpToRef.current) {
            speechQueue.push(text.slice(spokenUpToRef.current));
            spokenUpToRef.current = text.length;
        }
        if (!gateway.isStreaming && text.length === 0 && spokenUpToRef.current > 0) {
            // Strömmen är slut och innehållet flyttat till messages: läs upp resten.
            speechQueue.flush();
            spokenUpToRef.current = 0;
            spokenRef.current = false;
        }
    }, [gateway.streamingContent, gateway.isStreaming]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const inputDisabled = gateway.status !== 'connected';
    const alexOnline = gateway.status === 'connected';
    const alexBusy = gateway.isStreaming || gateway.alexState === 'thinking' || gateway.alexState === 'executing';

    // Vad som räknas som brus bor i chat/messageNoise.ts, med tester.
    const filteredGatewayMessages = useMemo(
        () => gateway.messages.filter(msg => !isNoiseMessage(msg)),
        [gateway.messages]
    );

    return (
        <div className="panel chat-panel chat-alex-mode">
            {/* Header */}
            <div className="chat-header">
                <div className="chat-mode-toggle">
                    <button className="chat-mode-btn active">
                        <span className={`gateway-dot ${alexOnline ? 'online' : 'offline'}`} />
                        <Zap size={13} />
                        Alex
                    </button>
                </div>

                <div className="alex-status-bar">
                    <span className={`alex-status-label ${gateway.status}`}>
                        {STATUS_LABELS[gateway.status] || gateway.status}
                    </span>
                    {alexOnline && gateway.alexState !== 'unknown' && (
                        <span className={`alex-state ${gateway.alexState}`}>
                            {ALEX_STATE_LABELS[gateway.alexState]}
                        </span>
                    )}
                </div>
            </div>

            {!alexOnline && (
                <div className="alex-offline-note" role="status">
                    <p>
                        Alex kan inte nås från den här enheten just nu. Kontrollera Tailscale och anslutningen
                        till servern. Din historik finns kvar. Meddelanden kan skickas när anslutningen är tillbaka.
                    </p>
                    {onUseCloudAlex && (
                        <button type="button" className="alex-switch-btn" onClick={onUseCloudAlex}>
                            <Cloud size={13} />
                            Prata med moln-Alex i stället
                        </button>
                    )}
                </div>
            )}
            {/* Messages */}
            <div className="chat-messages">
                {filteredGatewayMessages.length === 0 && !gateway.isStreaming ? (
                    <p className="empty">
                        {alexOnline ? 'Inget sagt än. Skriv något till Alex.' : 'Ansluter till Alex…'}
                    </p>
                ) : (
                    <>
                        {filteredGatewayMessages.map((msg, i) => (
                            <MemoMessage key={i} msg={msg} />
                        ))}
                        {gateway.isStreaming && gateway.streamingContent && (
                            <div className="chat-message assistant alex streaming">
                                <div className="message-content markdown-body">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{gateway.streamingContent}</ReactMarkdown>
                                    <span className="streaming-cursor" />
                                </div>
                            </div>
                        )}
                        {gateway.isStreaming && !gateway.streamingContent && (
                            <div className="chat-message assistant alex streaming">
                                <div className="message-content">
                                    <span className="thinking-dots">
                                        <span /><span /><span />
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* ─── Bolt-Style Input ─── */}
            <div className="bolt-input-wrapper">
                <div className="bolt-input-glow" />
                <div className="bolt-input-card">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder="Prata med Alex…"
                        disabled={inputDisabled}
                        className="bolt-textarea"
                        rows={1}
                    />

                    {/* Attachment preview strip */}
                    {attachments.length > 0 && (
                        <div className="attachment-strip">
                            {attachments.map(att => (
                                <div key={att.id} className={`attachment-chip ${att.preview ? 'has-preview' : ''}`}>
                                    {att.preview ? (
                                        <img src={att.preview} alt={att.name} className="attachment-thumb" />
                                    ) : (
                                        <>
                                            <FileIcon size={14} className="attachment-file-icon" />
                                            <span className="attachment-name">{att.name}</span>
                                            <span className="attachment-size">{(att.size / 1024).toFixed(0)}KB</span>
                                        </>
                                    )}
                                    <button className="attachment-remove" onClick={() => removeAttachment(att.id)} title="Ta bort bilaga">
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Hidden file inputs */}
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={e => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={e => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                    />

                    <div className="bolt-toolbar">
                        <div className="bolt-toolbar-left">
                            <AttachMenu
                                onImageSelect={() => imageInputRef.current?.click()}
                                onFileSelect={() => fileInputRef.current?.click()}
                            />
                            <span className="bolt-model-trigger">Alex · huvudagent</span>
                            {talk.supported && (
                                <button
                                    type="button"
                                    className={`alexdock-mic alexdock-mic--${queueSpeaking && talk.state === 'idle' ? 'speaking' : talk.state}`}
                                    title={queueSpeaking || talk.state === 'speaking' ? 'Tysta Alex' : 'Håll in och prata'}
                                    disabled={inputDisabled}
                                    onPointerDown={(e) => {
                                        e.preventDefault();
                                        if (queueSpeaking) { speechQueue.stop(); return; }
                                        if (talk.state === 'speaking') { talk.hush(); return; }
                                        void talk.start();
                                    }}
                                    onPointerUp={talk.stop}
                                    onPointerLeave={talk.stop}
                                    onPointerCancel={talk.stop}
                                    onContextMenu={(e) => e.preventDefault()}
                                >
                                    {queueSpeaking || talk.state === 'speaking'
                                        ? <Square size={13} strokeWidth={2.5} />
                                        : <Mic size={14} strokeWidth={2.25} />}
                                </button>
                            )}
                            {(talk.state !== 'idle' || queueSpeaking) && (
                                <span className="alex-voice-status">
                                    {talk.state !== 'idle' ? TALK_LABEL[talk.state] : 'Alex pratar…'}
                                </span>
                            )}
                        </div>

                        <div className="bolt-toolbar-right">
                            {alexBusy ? (
                                <button
                                    className="bolt-abort-btn"
                                    onClick={gateway.abortChat}
                                    title="Stop Alex"
                                >
                                    <Square size={12} />
                                    <span>Stop</span>
                                </button>
                            ) : (
                                <button
                                    className="bolt-send-btn"
                                    onClick={handleSend}
                                    disabled={inputDisabled || !input.trim()}
                                >
                                    <span>Skicka</span>
                                    <SendHorizontal size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
